/**
 * Конверт секретного сообщения — то, что лежит в `Message.envelope` контракта
 * (Base64). Сервер видит только его и не умеет читать.
 *
 * Двоичный формат v1 (все числа big-endian):
 *
 *   0      version = 1
 *   1      flags: бит 0 — есть X3DH-заголовок первого сообщения
 *   [X3DH] signingKey 32 ‖ identityKey 32 ‖ identitySignature 64 ‖ ephemeralKey 32
 *          ‖ signedPrekeyId u32 ‖ oneTimePrekeyId u32 (0xFFFFFFFF — без одноразового)
 *   далее  ratchetKey 32 ‖ previousCount u32 ‖ count u32
 *   далее  ciphertext AES-256-GCM (с тегом 16 байт в конце)
 *
 * AAD шифра = associatedData X3DH (IK_A ‖ IK_B) ‖ все байты заголовка до ciphertext.
 * Так подмена любого поля заголовка — в том числе X3DH-ключей — ломает тег.
 */
import { concat, fromBase64, readU32be, toBase64, u32be } from './bytes'
import { KEY_BYTES } from './primitives'
import type { RatchetHeader } from './ratchet'
import type { InitialHeader } from './x3dh'

export const ENVELOPE_VERSION = 1
const FLAG_INITIAL = 0x01
const NO_ONE_TIME = 0xffffffff
const SIGNATURE_BYTES = 64
const X3DH_BYTES = KEY_BYTES * 3 + SIGNATURE_BYTES + 8
const RATCHET_BYTES = KEY_BYTES + 8
const GCM_TAG_BYTES = 16

/** Верхняя граница Base64-строки в контракте (`SecretEnvelope.maxLength`). */
export const ENVELOPE_MAX_CHARS = 60000

export interface Envelope {
  initial: InitialHeader | null
  header: RatchetHeader
  ciphertext: Uint8Array
}

export function encodeInitial(initial: InitialHeader): Uint8Array {
  return concat(
    initial.signingKey,
    initial.identityKey,
    initial.identitySignature,
    initial.ephemeralKey,
    u32be(initial.signedPrekeyId),
    u32be(initial.oneTimePrekeyId ?? NO_ONE_TIME),
  )
}

export function encodeHeader(initial: InitialHeader | null, header: RatchetHeader): Uint8Array {
  const prefix = new Uint8Array([ENVELOPE_VERSION, initial ? FLAG_INITIAL : 0])
  return concat(
    prefix,
    initial ? encodeInitial(initial) : new Uint8Array(0),
    header.ratchetKey,
    u32be(header.previousCount),
    u32be(header.count),
  )
}

export function encodeEnvelope(envelope: Envelope): string {
  return toBase64(concat(encodeHeader(envelope.initial, envelope.header), envelope.ciphertext))
}

/** Бросает `secret_envelope_invalid` на любом расхождении формата — без частичного разбора. */
export function decodeEnvelope(text: string): Envelope & { headerBytes: Uint8Array } {
  if (typeof text !== 'string' || text.length === 0 || text.length > ENVELOPE_MAX_CHARS) {
    throw new Error('secret_envelope_invalid')
  }
  const bytes = fromBase64(text)
  if (bytes.length < 2 || bytes[0] !== ENVELOPE_VERSION) throw new Error('secret_envelope_invalid')
  const flags = bytes[1] ?? 0
  if ((flags & ~FLAG_INITIAL) !== 0) throw new Error('secret_envelope_invalid')
  let offset = 2
  let initial: InitialHeader | null = null
  if (flags & FLAG_INITIAL) {
    if (bytes.length < offset + X3DH_BYTES) throw new Error('secret_envelope_invalid')
    const signingKey = bytes.slice(offset, offset + KEY_BYTES)
    offset += KEY_BYTES
    const identityKey = bytes.slice(offset, offset + KEY_BYTES)
    offset += KEY_BYTES
    const identitySignature = bytes.slice(offset, offset + SIGNATURE_BYTES)
    offset += SIGNATURE_BYTES
    const ephemeralKey = bytes.slice(offset, offset + KEY_BYTES)
    offset += KEY_BYTES
    const signedPrekeyId = readU32be(bytes, offset)
    offset += 4
    const oneTime = readU32be(bytes, offset)
    offset += 4
    initial = {
      signingKey,
      identityKey,
      identitySignature,
      ephemeralKey,
      signedPrekeyId,
      oneTimePrekeyId: oneTime === NO_ONE_TIME ? null : oneTime,
    }
  }
  if (bytes.length < offset + RATCHET_BYTES + GCM_TAG_BYTES) throw new Error('secret_envelope_invalid')
  const ratchetKey = bytes.slice(offset, offset + KEY_BYTES)
  offset += KEY_BYTES
  const previousCount = readU32be(bytes, offset)
  offset += 4
  const count = readU32be(bytes, offset)
  offset += 4
  return {
    initial,
    header: { ratchetKey, previousCount, count },
    ciphertext: bytes.slice(offset),
    headerBytes: bytes.slice(0, offset),
  }
}
